 
import { Decimal } from 'decimal.js';

import {  LivePrice, PriceTables } from './types';

import { getCustomerPrice } from './getCustomerPrice';
import { ArticleData } from './index';

export type PriceData = {
	basePrice: number;
	salePrice: number | null;
	finalPrice: number;
	discount: number;
	discountPercentage: number;
	taxRate: number;
};

const getPriceTableByCustomerGroup = (
	customerGroupid?: string | null,
	priceTable?: PriceTables | null,
): string | null => {
	if (!priceTable && customerGroupid) {
		// https://agrantis.atlassian.net/browse/AG-4734
		// we assume that if we do not have mapping just use it as 1=1
		// so if no maaping we take customer group id to be the price table id
		return customerGroupid;
	}
	if (!priceTable || !customerGroupid) {
		return priceTable?.default;
	}

	if (!priceTable.mapping) {
		return null;
	}

	const priceTableByCustomerGroup = priceTable.mapping[customerGroupid];
	if (!priceTableByCustomerGroup) {
		return null;
	}

	return priceTableByCustomerGroup;
};

const STUK_PRICE_UNIT = 'stk';

const getPriceByCustomerGroupIdFromPricetable = (
	item?: ArticleData,
	priceTableId?: string | null,
) => {
	if (!item || !priceTableId) return null;
	const customerGroupIdPrice = item?.item.erpData?.prices?.find(
		(p) => priceTableId !== null && p?.priceListID === priceTableId?.toString(),
	);

	// if its STUK we dont do multiplication by amount
	if (item.item.erpData?.prices?.[0]?.priceUnit === STUK_PRICE_UNIT) {
		return new Decimal(customerGroupIdPrice?.price ?? 0).toNumber();
	}

	// otherwise we do multiplication by amount
	return new Decimal(customerGroupIdPrice?.price ?? 0)
		.mul(item?.amount ?? 1)
		.toNumber();
};

const getCustomerIndividualPrice = (item?: ArticleData) => {
	if (!item) return null;
	const customerPrice = item?.customerPrice
		? getCustomerPrice(item?.customerPrice)
		: null;
	return customerPrice;
};

type getArticlePriceDataProps = {
	item?: ArticleData;
	livePrice?: LivePrice;
	retailerPriceTables?: PriceTables | null;
	customerGroupId?: string | null;
};

export function getArticlePriceData(
	params: getArticlePriceDataProps,
): PriceData {
	const { item, livePrice, retailerPriceTables, customerGroupId } = params;
	const priceTableId = getPriceTableByCustomerGroup(
		customerGroupId,
		retailerPriceTables,
	);

	const price = getPriceByCustomerGroupIdFromPricetable(item, priceTableId);
	const customerIndividualPrice = getCustomerIndividualPrice(item);

	const basePrice = price ?? item?.item.basePrice ?? 0;
	const salePrice = item?.item.salePrice || null; // Should evaluate 0 to null!!

	const finalPrice =
		livePrice?.price ?? customerIndividualPrice ?? salePrice ?? basePrice;

	const discount = basePrice - finalPrice;
	const discountPercentage = (discount / basePrice) * 100;
	const taxRate = item?.item.taxRate ?? 0;

	return {
		basePrice,
		salePrice,
		finalPrice,
		discount,
		discountPercentage,
		taxRate,
	};
}
